import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DollarSign } from 'lucide-react';

type HTMLInput = React.InputHTMLAttributes<HTMLInputElement>;

interface InputCurrencyProps
  extends Omit<HTMLInput, 'onChange' | 'value' | 'defaultValue'> {
  className?: string;
  error?: boolean;
  currency?: string; // e.g. 'USD' | 'ARS'
  locale?: string; // e.g. 'en-US' | 'es-AR'
  value?: number | null; // controlled numeric value
  defaultValue?: number | null;
  onValueChange?: (value: number | null) => void;
  onChange?: (value: number | null) => void; // keep for backward compat
}

export const InputCurrency: FC<InputCurrencyProps> = ({
  className = '',
  placeholder = '0,00',
  error = false,
  currency = 'USD',
  locale = 'en-US',
  onValueChange,
  onChange,
  value,
  defaultValue,
  ...props
}) => {
  const [focused, setFocused] = useState(false);
  const [display, setDisplay] = useState<string>(''); // what user sees
  const [numeric, setNumeric] = useState<number | null>(null);
  const isControlled = value !== undefined;
  const justMounted = useRef(true);

  // Get locale symbols once
  const parts = useMemo(() => {
    const nf = new Intl.NumberFormat(locale, { style: 'currency', currency });
    const p = nf.formatToParts(12345.6);
    const group = p.find((x) => x.type === 'group')?.value ?? ',';
    const decimal = p.find((x) => x.type === 'decimal')?.value ?? '.';
    return { group, decimal, nf };
  }, [locale, currency]);

  const formatCurrency = useCallback((n: number) => parts.nf.format(n), [parts.nf]);

  // Convert a typed string to a number respecting locale
  const toNumber = (text: string): number | null => {
    if (!text) return null;

    // normalize: remove spaces & currency/letters, keep digits, group, decimal, minus
    let s = text.replace(/[^\d\-+.,]/g, '');

    // allow either '.' or ',' as decimal from user; convert locale-decimal to '.'
    if (parts.group) s = s.split(parts.group).join(''); // remove thousands
    s = s.replace(',', '.'); // unify decimal
    // keep only first '.' and first leading '-' sign
    s = s.replace(/(?!^)-/g, '').replace(/(\..*)\./g, '$1');

    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  };

  // Initialize
  useEffect(() => {
    if (justMounted.current) {
      justMounted.current = false;
      const initial = isControlled ? (value ?? null) : (defaultValue ?? null);
      if (initial !== null) {
        setNumeric(initial);
        setDisplay(formatCurrency(initial));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync from controlled value (but don’t fight user while focused)
  useEffect(() => {
    if (!isControlled) return;
    if (focused) return;
    const next = value ?? null;
    setNumeric(next);
    setDisplay(next === null ? '' : formatCurrency(next));
  }, [isControlled, value, focused, formatCurrency]);

  const emitChange = (n: number | null) => {
    setNumeric(n);
    onValueChange?.(n);
    onChange?.(n);
  };

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;

    // allow empty or lone '-' while typing
    if (raw === '' || raw === '-' || raw === parts.decimal) {
      setDisplay(raw);
      emitChange(null);
      return;
    }

    setDisplay(raw);
    const n = toNumber(raw);
    emitChange(n);
  };

  const handleFocus = () => {
    setFocused(true);
    // On focus: show a raw editable number (remove currency and groups).
    // When the value is 0 (treated as "unset" in most forms), clear the display so the user
    // can start typing without dragging a leading zero — the placeholder shines through.
    if (numeric !== null && numeric !== 0) {
      // Turn 12345.6 into locale-ish editable string using locale decimal
      const text = numeric.toString().replace('.', parts.decimal);
      setDisplay(text);
    } else {
      setDisplay('');
    }
  };

  const handleBlur = () => {
    setFocused(false);
    // On blur: pretty format if there’s a valid number
    if (numeric !== null) {
      setDisplay(formatCurrency(numeric));
    } else {
      setDisplay('');
    }
  };

  return (
    <div className="relative">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <DollarSign className="h-5 w-5 text-gray-400 dark:text-gray-500" />
      </div>
      <input
        type="text"
        inputMode="decimal"
        className={`block w-full border rounded-md pl-10 pr-4 p-2 placeholder:text-gray-400 dark:placeholder:text-gray-500 placeholder:text-sm
          text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-800 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200 ${
            error
              ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
              : 'border-gray-300 dark:border-gray-600'
          } ${className}`}
        placeholder={placeholder}
        value={display}
        onChange={handleInput}
        onFocus={handleFocus}
        onBlur={handleBlur}
        {...props}
      />
    </div>
  );
};
